import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  mayUseDeveloperMode,
  type ApiFailure,
  type PermissionAnomaly,
  type PermissionGrant,
  type PermissionUser,
  type AdminReport,
} from '../api/types'
import type { Api } from '../api'
import {
  Badge,
  Card,
  CountStat,
  EmptyState,
  Loading,
  Notice,
  PageTabs,
  PersonCell,
  SearchInput,
} from '../components/ui'
import { DataGrid, type Column } from '../components/DataGrid'
import { FailureState } from '../components/FailureState'
import { SlideOver } from '../components/SlideOver'
import { PlatformRolesTab, ReportAccessTab } from './AccessAdmin'
import { AccessPeopleTab, type PersonSection } from './AccessPeopleTab'
import { AccessApprovalsTab } from './AccessApprovalsTab'
import { AccessHrRolesTab } from './AccessHrRolesTab'
import { AccessProblemsTab, problemsCount } from './AccessProblemsTab'
import { AccessSyncTab } from './AccessSyncTab'
import { ColumnDefinitionV2 } from './ColumnDefinitionV2'
import { ColumnGovernanceTab } from './ColumnGovernanceTab'
import { DeveloperModeTab } from './DeveloperModeTab'

/**
 * ACCESS — requirement R4.
 *
 * ===========================================================================
 * WHAT IS EDITABLE HERE AND WHAT IS NOT
 *
 *   row scope       which companies, units, teams   D365 — read-only, always
 *   report access   may they open this at all       ours — granted here
 *   platform role   admin / permission manager /    ours — granted here
 *                   master viewer
 *   column access   which permission unlocks each   ours — set here
 *                   FIELD of the report
 *
 * ROW SCOPE belongs to the ERP. The platform enforces directly from
 * D365_UserPermDetails, so writing a second copy here would let this screen
 * report a change nobody's access reflects — the worst possible behaviour for a
 * screen whose entire job is to be trusted.
 *
 * COLUMN ACCESS is a split, and the split is the point. D365 says which MODULES
 * a person holds (AR_Mod, SLS_Mod, Fin_Mod) and that half is read-only. Which
 * of THIS platform's columns each module unlocks lives in
 * plat.Report_Column_Permission_MBI and is ours — the ERP has no opinion about a
 * column called "121 To 180 Days". An earlier version of this comment listed
 * column access as wholly D365's, which was half right and made the mapping
 * look untouchable.
 *
 * ===========================================================================
 * WHY THIS IS THREE TABS AND NOT ONE SCROLLING PAGE
 *
 * It was one page: an anomalies card, two always-open grant forms, a people
 * grid, a detail card that opened BELOW the grid, and a paragraph at the
 * bottom. Five things of equal visual weight, so nothing led, and the two
 * questions an administrator actually arrives with —
 *
 *     "who can open this report?"      and      "why can this person see that?"
 *
 * — were answered by different parts of the same long scroll. Clicking a person
 * near the top rendered their grants off the bottom of the window.
 *
 * Three tabs, because there are exactly three jobs and they are done by
 * different people at different times:
 *
 *     Report access   grant and revoke        System Admin, Permission Manager
 *     Platform roles  appoint and revoke      System Admin
 *     Data scope      read D365, diagnose     both, and it changes nothing
 *
 * The two that GRANT come first. The old page put the read-only review first,
 * which is why "where do I give someone permission?" had no obvious answer.
 *
 * ===========================================================================
 * ONE SCROLLING REGION, AND IT IS THE TABLE
 *
 * The header, the tab rail and each tab's toolbar are chrome and stay put; the
 * grid owns the only scrollbar. Same rule as the Receivables report, and it was
 * asked for there for the same reason: scrolling a long list must not carry the
 * controls off the screen.
 */

type TabId = 'people' | 'approvals' | 'hrroles' | 'problems' | 'sync' | 'access' | 'roles' | 'columns' | 'scope' | 'developer'

export function PermissionsPage({
  api,
  roles,
  actor,
  isV2 = false,
  account = null,
  openSync = 0,
  onSyncOpened,
  onLoginAs,
}: {
  api: Api
  roles: string[]
  actor: string | null
  /** The account this session is: the person acted as under Login As, else actor. */
  account?: string | null
  /** Above zero: open Mindzer sync ("See Mindzer sync" on the stale-data strip). */
  openSync?: number
  /** The request was used: it must not reopen Mindzer sync on the next visit. */
  onSyncOpened?: () => void
  /** Login As from a person's page; absent for an admin who may not act (D7). */
  onLoginAs?: (oid: string) => void
  /**
   * The database runs the new permission model. Report access and Data scope
   * read today's tables, which decide nothing then, so they are not offered
   * (the API answers them 410); Column access edits the new rules (060).
   */
  isV2?: boolean
}) {
  // Entitlement is the platform ROLE, never data scope: a Permission Manager's
  // whole job is this screen and they may hold no data access at all.
  //
  // Hiding a tab is presentation, not protection. Every endpoint resolves the
  // caller from the database and refuses independently, and so does each
  // procedure — editing this in a debugger produces a 403, not a grant.
  const canGrantAccess = roles.includes('SystemAdmin')
  const isSystemAdmin = roles.includes('SystemAdmin')
  // Developer mode is System Development's too (D5): for them it is the only tab.
  const canDevMode = mayUseDeveloperMode(roles)

  /* TAB COUNTS only where the number asks for action (05 section 7.2): Approvals,
     the people who tried to sign in and wait; Problems, the open items. Waiting, HR
     main scope changed, personal settings and new manager links are listed there under
     Information and left out of that count (D54, D59): at the switch everyone waits,
     and Approvals carries it; the others say how things are, not what is wrong. Read
     when the page opens and after a person's
     page changes something; the two tabs report the counts they read for
     themselves, so opening one costs no second read. A failure leaves the tab
     without a number. */
  const [counts, setCounts] = useState<{ approvals: number | null; problems: number | null }>({ approvals: null, problems: null })
  const [recounts, setRecounts] = useState(0)
  const recount = useCallback(() => setRecounts((n) => n + 1), [])
  const approvalsCounted = useCallback((n: number | null) => setCounts((c) => ({ ...c, approvals: n })), [])
  const problemsCounted = useCallback((n: number | null) => setCounts((c) => ({ ...c, problems: n })), [])

  const tabs = useMemo(
    () =>
      [
        // The new model's own screens come first: who can enter, and what each
        // person sees (05 section 7.2). Approvals, HR roles, Problems and Mindzer
        // sync join them part by part.
        // Tab names: the owner's, 28 Oct 2026 (People, Approvals, Problems, Mindzer sync and
        // Column access renamed). The ids stay, so links and remembered tabs keep working.
        isSystemAdmin && isV2 && { id: 'people' as const, label: 'Users' },
        isSystemAdmin && isV2 && { id: 'approvals' as const, label: 'Access approvals', count: counts.approvals },
        isSystemAdmin && isV2 && { id: 'hrroles' as const, label: 'HR Roles' },
        // "Data Quality" since it holds Problems and Information (owner, 30 Oct 2026; was "Data quality problems").
        isSystemAdmin && isV2 && { id: 'problems' as const, label: 'Data Quality', count: counts.problems },
        isSystemAdmin && isV2 && { id: 'sync' as const, label: 'Mindzer sync report' },
        canGrantAccess && !isV2 && { id: 'access' as const, label: 'Report access' },
        isSystemAdmin && { id: 'roles' as const, label: 'Platform roles' },
        // Under the new model a System Admin's alone (060, D53), as the API refuses anyone else.
        (isV2 ? isSystemAdmin : canGrantAccess) && { id: 'columns' as const, label: 'Column definition' },
        canGrantAccess && !isV2 && { id: 'scope' as const, label: 'Data scope' },
        // Last. It is the one tab that changes nothing about who can see what --
        // it changes how much the platform explains about itself -- and the one
        // System Development sees (D5).
        canDevMode && { id: 'developer' as const, label: 'Developer mode' },
      ].filter(Boolean) as { id: TabId; label: string; count?: number | null }[],
    [canGrantAccess, isSystemAdmin, canDevMode, isV2, counts],
  )

  // The tab shown is the one picked while it is still offered, else the first.
  // App re-reads /me after a refusal, so the model can turn v2 while this page is
  // open and take Report access and Data scope away under the reader.
  const [picked, setTab] = useState<TabId>(openSync ? 'sync' : (tabs[0]?.id ?? 'columns'))
  const tab = tabs.some((t) => t.id === picked) ? picked : (tabs[0]?.id ?? picked)

  // "See Mindzer sync" on the stale-data strip, from whichever page it was on.
  useEffect(() => {
    if (!openSync) return
    setTab('sync')
    onSyncOpened?.()
  }, [openSync, onSyncOpened])

  useEffect(() => {
    if (!isSystemAdmin || !isV2) return
    let cancelled = false
    // One row: only the unfiltered count is wanted.
    api
      .accessApprovals({ view: 'tried', top: 1 })
      .then((a) => !cancelled && approvalsCounted(a.tried))
      .catch(() => !cancelled && approvalsCounted(null))
    api
      .accessProblems()
      .then((p) => !cancelled && problemsCounted(problemsCount(p)))
      .catch(() => !cancelled && problemsCounted(null))
    return () => {
      cancelled = true
    }
  }, [api, isSystemAdmin, isV2, recounts, approvalsCounted, problemsCounted])
  // "Open their page →" on an approval card: People, with that person open.
  const [openRequest, setOpenRequest] = useState<{ id: number; seq: number; section?: PersonSection } | null>(null)
  const openPerson = (id: number, section?: PersonSection) => {
    setOpenRequest((r) => ({ id, seq: (r?.seq ?? 0) + 1, section }))
    setTab('people')
  }
  // Used once: a later visit to People must not reopen that person.
  const openUsed = useCallback(() => setOpenRequest(null), [])

  /* THE CHOSEN REPORT, HELD AT THE PAGE so it survives switching tabs.
     ------------------------------------------------------------------------
     Report access and Column access are both scoped to one report; Platform
     roles and Data scope are not. So the picker is rendered by the two tabs
     that need it rather than in the page header — where it would sit above two
     screens it has no bearing on — but the CHOICE lives here, so moving between
     the two scoped tabs does not silently reset which report you are editing.

     The registry is fetched once, here, for the same reason: both tabs need the
     same list and it does not change while the page is open. */
  const [reports, setReports] = useState<AdminReport[] | null>(null)
  const [reportSlug, setReportSlug] = useState<string>('')
  // Kept, not swallowed. With no registry the slug stays '', both scoped tabs
  // return before they ever call the API, and each sat on "Reading…" forever
  // with no reference to quote. The refusal is shown in their place instead.
  const [reportsFailure, setReportsFailure] = useState<ApiFailure | null>(null)

  useEffect(() => {
    if (!canGrantAccess) return
    let cancelled = false
    api
      .adminReports()
      .then((r) => {
        if (cancelled) return
        // The scoped tabs (Report access, Column access) only apply to DATA
        // reports. The admin module ("Access" itself) is role-scoped — no
        // report-access list, no governed columns — so it must not appear as a
        // pickable report here.
        const dataReports = r.filter((x) => x.scopingModel !== 'role-scoped')
        setReports(dataReports)
        // No default beyond "the first one registered". Guessing a slug is what
        // the API used to do, and it is how somebody edits the wrong report.
        setReportSlug((s) => s || dataReports[0]?.slug || '')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setReports([])
        setReportsFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
      })
    return () => {
      cancelled = true
    }
  }, [api, canGrantAccess])

  return (
    <div className="stack-tight flex h-full min-h-0 flex-col gap-4">
      <header className="shrink-0">
        <div className="eyebrow">Governance</div>
        <h1 className="mt-1 text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">
          Access
        </h1>
        {isV2 ? (
          <p className="mt-1.5 max-w-3xl text-(length:--text-base) leading-relaxed text-(--color-ink-3)">
            Who can enter MBI, and what each user sees.
          </p>
        ) : (
        <p className="mt-1.5 max-w-3xl text-(length:--text-base) leading-relaxed text-(--color-ink-3)">
          Four gates decide what somebody sees, and three of them are this platform&rsquo;s: who may{' '}
          <span className="font-medium text-(--color-ink-2)">open a report</span>, who{' '}
          <span className="font-medium text-(--color-ink-2)">administers the platform</span>, and
          which permission unlocks each{' '}
          <span className="font-medium text-(--color-ink-2)">column</span>. D365 owns the fourth
          &mdash; which rows a user gets &mdash; and it is read here rather than set.
        </p>
        )}
      </header>

      <PageTabs tabs={tabs} value={tab} onChange={setTab} label="Access administration" />

      {/* The scoped tabs render nothing until the registry has answered: they
          cannot ask "who may open ?" and an empty slug would be a 400. If the
          registry REFUSED, that refusal is the answer for both of them. */}
      {reportsFailure && canGrantAccess && (tab === 'access' || tab === 'columns') && (
        <FailureState failure={reportsFailure} />
      )}
      {tab === 'people' && isSystemAdmin && isV2 && (
        <AccessPeopleTab api={api} openRequest={openRequest} onOpened={openUsed} onChanged={recount} actor={actor} onLoginAs={onLoginAs} />
      )}
      {tab === 'approvals' && isSystemAdmin && isV2 && <AccessApprovalsTab api={api} onOpenPerson={openPerson} onCount={approvalsCounted} />}
      {tab === 'hrroles' && isSystemAdmin && isV2 && <AccessHrRolesTab api={api} />}
      {tab === 'problems' && isSystemAdmin && isV2 && <AccessProblemsTab api={api} onOpenPerson={openPerson} onCount={problemsCounted} />}
      {tab === 'sync' && isSystemAdmin && isV2 && <AccessSyncTab api={api} />}
      {tab === 'access' && canGrantAccess && !isV2 && !reportsFailure && (
        <ReportAccessTab
          api={api}
          reports={reports}
          reportSlug={reportSlug}
          onPickReport={setReportSlug}
        />
      )}
      {tab === 'roles' && isSystemAdmin && <PlatformRolesTab api={api} actor={actor} account={account} isV2={isV2} />}
      {tab === 'columns' && isV2 && isSystemAdmin && !reportsFailure && (
        // Column groups and who sees each column (D53, owner 28 Oct 2026).
        <ColumnDefinitionV2 api={api} reports={reports} reportSlug={reportSlug} onPickReport={setReportSlug} />
      )}
      {tab === 'columns' && !isV2 && canGrantAccess && !reportsFailure && (
        <ColumnGovernanceTab
          api={api}
          canEdit={isSystemAdmin}
          reports={reports}
          reportSlug={reportSlug}
          onPickReport={setReportSlug}
        />
      )}
      {tab === 'scope' && canGrantAccess && !isV2 && <DataScopeTab api={api} />}
      {tab === 'developer' && canDevMode && <DeveloperModeTab api={api} />}
    </div>
  )
}

/* =========================================================================
   DATA SCOPE — the D365 review. Read-only, and says so once, at the top.
   ========================================================================= */

function DataScopeTab({ api }: { api: Api }) {
  const [users, setUsers] = useState<PermissionUser[] | null>(null)
  const [anomalies, setAnomalies] = useState<PermissionAnomaly[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<PermissionUser | null>(null)
  const [detail, setDetail] = useState<PermissionGrant[] | null>(null)
  const [showAllFindings, setShowAllFindings] = useState(false)
  // The two side fetches keep their own failures rather than dropping them.
  // Neither may take the page down (the people grid is still true), but a
  // swallowed anomalies error read as "D365's data is clean", and a swallowed
  // detail error left the panel on "Loading grants…" for good.
  const [anomaliesFailure, setAnomaliesFailure] = useState<ApiFailure | null>(null)
  const [detailFailure, setDetailFailure] = useState<ApiFailure | null>(null)

  useEffect(() => {
    let cancelled = false
    // Debounced so typing a name does not fire a query per keystroke against a
    // view that costs 1–3s of CPU to read.
    const t = setTimeout(() => {
      api
        .permissionUsers(search || undefined)
        .then((u) => !cancelled && setUsers(u))
        .catch((e: unknown) =>
          !cancelled &&
          setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }),
        )
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [api, search])

  useEffect(() => {
    let cancelled = false
    api
      .permissionAnomalies()
      .then((a) => !cancelled && setAnomalies(a))
      .catch((e: unknown) =>
        !cancelled &&
        setAnomaliesFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }),
      )
    return () => {
      cancelled = true
    }
  }, [api])

  useEffect(() => {
    if (!selected) {
      setDetail(null)
      setDetailFailure(null)
      return
    }
    let cancelled = false
    setDetail(null)
    setDetailFailure(null)
    api
      .permissionUserDetail(selected.email)
      .then((d) => !cancelled && setDetail(d))
      .catch((e: unknown) =>
        !cancelled &&
        setDetailFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }),
      )
    return () => {
      cancelled = true
    }
  }, [api, selected])

  if (failure) return <FailureState failure={failure} />

  const findings = anomalies ?? []
  const broken = findings.filter((a) => a.severity === 'broken').length

  const columns: Column<PermissionUser>[] = [
    {
      key: 'person',
      header: 'User',
      align: 'left',
      // A rendered cell cannot be compared, so the sort is given the name the
      // reader actually sees. Falls back to the email for a principal who has
      // signed in but carries no display name yet.
      sortValue: (u) => u.displayName ?? u.email,
      render: (u) => <PersonCell name={u.displayName} email={u.email} />,
    },
    { key: 'userClass', header: 'User Class', align: 'left' },
    {
      key: 'breadth',
      header: 'Breadth',
      align: 'left',
      // Sorts by the fact, not by the badge: whole-company first descending,
      // which is the order an administrator scanning for over-broad scope
      // wants. The rendered cell is a Badge and cannot be compared.
      sortValue: (u) => (u.wholeCompany ? 1 : 0),
      // The single most consequential fact about a person's scope.
      //
      // There is no middle "whole unit" state: it was derived from the old
      // table's 'ALL' wildcard, and D365_UserPermDetails has none. Breadth is
      // either "holds every team in a company" or "holds specific teams", which
      // is what the data can actually answer.
      render: (u) =>
        u.wholeCompany ? <Badge tone="warn">Whole company</Badge> : <span className="text-(--color-ink-3)">Specific teams</span>,
    },
    { key: 'companies', header: 'Companies', align: 'right', render: (u) => String(u.companies) },
    { key: 'grantRows', header: 'Grants', align: 'right', render: (u) => String(u.grantRows) },
    {
      key: 'duplicateRows',
      header: 'Duplicates',
      align: 'right',
      // Surfaced because 77 people in production have them. Harmless to what
      // they see — the row filter uses EXISTS — but they are exactly what
      // breaks any report written with a JOIN, so an admin should find them.
      render: (u) =>
        u.duplicateRows > 0 ? (
          <span className="font-medium text-(--color-warn)" title="Duplicate grant rows in the source">
            {u.duplicateRows}
          </span>
        ) : (
          <span className="text-(--color-ink-4)">—</span>
        ),
    },
  ]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* SAID ONCE, AT THE TOP, WHERE IT CHANGES WHAT YOU EXPECT.
          It used to be the last paragraph on the page, under everything — the
          position reserved for things nobody reads. It is not a disclaimer; it
          is the reason this tab has no buttons on it. */}
      <div className="shrink-0">
        <Notice tone="info">
          <strong className="font-semibold text-(--color-ink)">Read-only, permanently.</strong> Scope
          and columns come from D365, which the platform enforces from directly — a second place to
          change them would be a second answer to the same question. Wider scope is granted in D365
          and arrives here on the next read.
        </Notice>
      </div>

      {/* FINDINGS ARE ONE LINE, NOT A CARD.
          They were a full card listing four findings, and on a 900px-tall
          window that card plus the read-only notice filled the viewport — the
          people table, which is the entire point of the tab, was pushed off the
          bottom of the screen. Diagnostics about a list must not displace the
          list. So the count sits in one row and the findings themselves open in
          a panel, where all of them fit and scroll rather than the first four
          being shown and the rest silently dropped. */}
      {/* A failed check is not "no findings", and must not look like it: with no
          line here the missing strip reads as a clean bill from D365. */}
      {anomaliesFailure && (
        <div className="shrink-0">
          <Notice tone="warn">
            Findings in D365&rsquo;s permission data could not be checked. This is not the same as
            none
            {'correlationId' in anomaliesFailure && anomaliesFailure.correlationId
              ? ` (reference ${anomaliesFailure.correlationId})`
              : ''}
            .
          </Notice>
        </div>
      )}
      {findings.length > 0 && (
        <div className="shrink-0">
          <button
            type="button"
            onClick={() => setShowAllFindings(true)}
            className="flex w-full items-center gap-3 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-2.5 text-left shadow-(--shadow-card) transition-colors hover:border-(--color-line-strong) hover:bg-(--color-surface-2)"
          >
            <span
              aria-hidden
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${broken > 0 ? 'bg-(--color-overdue)' : 'bg-(--color-warn)'}`}
            />
            <span className="flex-1 text-(length:--text-small) text-(--color-ink-2)">
              <span className="font-semibold text-(--color-ink)">
                {findings.length} finding{findings.length === 1 ? '' : 's'}
              </span>{' '}
              in D365&rsquo;s permission data
              {broken > 0 && (
                <>
                  {' — '}
                  <span className="font-semibold text-(--color-overdue)">
                    {broken} {broken === 1 ? 'matches' : 'match'} nobody who signs in
                  </span>
                </>
              )}
            </span>
            <span className="shrink-0 text-(length:--text-small) font-medium text-(--color-accent)">
              Review
            </span>
          </button>
        </div>
      )}

      {showAllFindings && (
        <SlideOver
          title="Findings in D365’s permission data"
          description="Read from the ERP, and fixed there. Nothing on this list can be corrected from MBI."
          onClose={() => setShowAllFindings(false)}
        >
          <ul className="space-y-3">
            {findings.map((a, i) => (
              <li key={i} className="flex items-start gap-3 rounded-md border border-(--color-line) px-3.5 py-3">
                <span
                  aria-hidden
                  className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                    a.severity === 'broken'
                      ? 'bg-(--color-overdue)'
                      : a.severity === 'review'
                        ? 'bg-(--color-warn)'
                        : 'bg-(--color-ink-4)'
                  }`}
                />
                <div className="min-w-0">
                  <div className="text-(length:--text-body) font-medium text-(--color-ink)">
                    {a.finding} — <span className="font-normal text-(--color-ink-2)">{a.subject}</span>
                  </div>
                  <div className="mt-0.5 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
                    {a.detail}
                  </div>
                  {/* The action, set apart: it is the only line here anybody
                      can act on, and it names work for the ERP team. */}
                  <div className="mt-1.5 border-t border-(--color-line) pt-1.5 text-(length:--text-small) font-medium text-(--color-ink-2)">
                    {a.action}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </SlideOver>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-(--color-line) px-5 py-3">
          <div className="flex flex-wrap items-stretch gap-2">
            <CountStat label="users with scope in D365" value={users?.length ?? '—'} />
            <CountStat
              label="hold a whole company"
              value={users?.filter((u) => u.wholeCompany).length ?? '—'}
              tone="warn"
            />
          </div>
          <div className="w-64">
            <SearchInput
              label="Search users in D365"
              placeholder="Search name or email…"
              value={search}
              onChange={setSearch}
            />
          </div>
        </div>

        {users === null ? (
          <Loading label="Reading the security table…" />
        ) : users.length === 0 ? (
          <EmptyState
            title={search ? `Nobody matches “${search}”.` : 'No permission rows found.'}
            detail="Only users with at least one grant in D365 appear here. Somebody absent from this list sees nothing, whatever report access they hold."
          />
        ) : (
          <DataGrid
            fill
            columns={columns}
            rows={users}
            onRowClick={setSelected}
            isRowActive={(u) => u.email === selected?.email}
          />
        )}

        <div className="shrink-0 border-t border-(--color-line) bg-(--color-surface-2) px-5 py-2 text-(length:--text-small) text-(--color-ink-3)">
          Select a user to see every grant, exactly as D365 stores it.
        </div>
      </Card>

      {/* IN A PANEL, NOT BELOW THE TABLE.
          The detail used to render underneath the grid, so clicking somebody
          near the top opened their grants off the bottom of the window — the
          reader clicked, nothing appeared to happen, and the answer was a
          scroll away. */}
      {selected && (
        <SlideOver
          width="lg"
          title={selected.displayName || selected.email}
          description={
            <>
              {selected.email} — every scope grant, exactly as D365 stores it.{' '}
              {selected.duplicateRows > 0 && (
                <span className="text-(--color-warn)">
                  {selected.duplicateRows} duplicate row
                  {selected.duplicateRows === 1 ? '' : 's'}; they do not double anything, because the
                  row filter matches with EXISTS.
                </span>
              )}
            </>
          }
          onClose={() => setSelected(null)}
        >
          {detailFailure ? (
            <FailureState failure={detailFailure} />
          ) : detail === null ? (
            <p className="py-10 text-center text-(length:--text-small) text-(--color-ink-3)">
              Loading grants…
            </p>
          ) : detail.length === 0 ? (
            <EmptyState
              title="No grants."
              detail="This user appears in the permission table but holds no company, unit or team — so they see nothing, whatever report access they have."
            />
          ) : (
            <DataGrid
              columns={[
                { key: 'company', header: 'Company', align: 'left' },
                { key: 'companyName', header: 'Company Name', align: 'left' },
                { key: 'businessUnit', header: 'Business Unit', align: 'left' },
                { key: 'businessTeam', header: 'Business Team', align: 'left' },
                { key: 'breadth', header: 'Breadth', align: 'left' },
              ]}
              rows={detail}
              emptyMessage="No grants."
            />
          )}
        </SlideOver>
      )}
    </div>
  )
}
